import type { UserAudience } from 'hooks/user'

/** Keep in sync with acc.audience_rank.AUDIENCE_RANK. */
export const AUDIENCE_RANK: Record<UserAudience, number> = {
  developer: 50,
  fleet_ops: 40,
  reseller: 30,
  facility_manager: 20,
  serviceman: 10,
  internal_serviceman: 10,
}

/** Product IAM never creates developer. Fleet Ops is assignable by a higher role. */
export const PRODUCT_IAM_AUDIENCES: UserAudience[] = [
  'fleet_ops',
  'reseller',
  'facility_manager',
  'serviceman',
  'internal_serviceman',
]

export const ALL_AUDIENCES: UserAudience[] = [
  'developer',
  'fleet_ops',
  'reseller',
  'facility_manager',
  'serviceman',
  'internal_serviceman',
]

export type AssignableAudienceOptions = {
  canAssignInternalServiceman?: boolean
}

export function assignableAudiences(
  actor: UserAudience | null,
  options?: AssignableAudienceOptions
): UserAudience[] {
  const rank = actor ? AUDIENCE_RANK[actor] ?? 0 : 0
  const allowInternal = options?.canAssignInternalServiceman === true
  return PRODUCT_IAM_AUDIENCES.filter(audience => {
    if ((AUDIENCE_RANK[audience] ?? 0) >= rank) return false
    if (audience === 'internal_serviceman' && !allowInternal) return false
    return true
  })
}

export function canAssignDirectRobots(audience: UserAudience | '' | null): boolean {
  return (
    audience === 'reseller' ||
    audience === 'facility_manager' ||
    audience === 'internal_serviceman'
  )
}

export function defaultAssignableAudience(
  actor: UserAudience | null,
  options?: AssignableAudienceOptions
): UserAudience | '' {
  const assignable = assignableAudiences(actor, options)
  if (assignable.includes('serviceman')) return 'serviceman'
  return assignable[0] || ''
}

export function generateTempPassword(length = 12): string {
  const lower = 'abcdefghijkmnpqrstuvwxyz'
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const digits = '23456789'
  const special = '!@#$%&*'
  const all = lower + upper + digits + special
  const pick = (alphabet: string) => alphabet[randomInt(alphabet.length)]
  const chars = [pick(lower), pick(upper), pick(digits), pick(special)]
  while (chars.length < length) {
    chars.push(pick(all))
  }
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomInt(i + 1)
    ;[chars[i], chars[j]] = [chars[j], chars[i]]
  }
  return chars.join('')
}

function randomInt(max: number): number {
  const webCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined
  if (webCrypto?.getRandomValues) {
    const buf = new Uint32Array(1)
    webCrypto.getRandomValues(buf)
    return buf[0] % max
  }
  return Math.floor(Math.random() * max)
}
